import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AppLayout from '@cloudscape-design/components/app-layout';
import ContentLayout from '@cloudscape-design/components/content-layout';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Spinner from '@cloudscape-design/components/spinner';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import Link from '@cloudscape-design/components/link';
import Modal from '@cloudscape-design/components/modal';
import FormField from '@cloudscape-design/components/form-field';
import Input from '@cloudscape-design/components/input';
import Table from '@cloudscape-design/components/table';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import { listPipelines, getMetrics, submitFeedback, MetricsResponse, submitAnalysisRequest, getAnalysisRequests, updateRequestStatus, deleteAnalysisRequest, AnalysisRequest } from '../apis/metricsApi';
import { MetricsDashboard } from '../components/MetricsDashboard';
import { PipelineSelector } from '../components/PipelineSelector';

const MetricsRecommenderPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [pipelines, setPipelines] = useState<string[]>([]);
  const [selected, setSelected] = useState<string>(searchParams.get('pipeline') || '');
  const [data, setData] = useState<MetricsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [requestModalVisible, setRequestModalVisible] = useState(false);
  const [viewRequestsVisible, setViewRequestsVisible] = useState(false);
  const [requestPipeline, setRequestPipeline] = useState('');
  const [requestAlias, setRequestAlias] = useState('');
  const [requests, setRequests] = useState<AnalysisRequest[]>([]);
  const [requestSubmitting, setRequestSubmitting] = useState(false);
  const [requestSuccess, setRequestSuccess] = useState(false);
  const [requestError, setRequestError] = useState('');

  useEffect(() => {
    getAnalysisRequests().then(setRequests);
  }, []);

  const handleSelect = (pipeline: string) => {
    setSelected(pipeline);
    if (pipeline) {
      setSearchParams({ pipeline });
    } else {
      setSearchParams({});
    }
  };

  useEffect(() => {
    listPipelines().then(setPipelines).catch(e => setError(e.message));
  }, []);

  useEffect(() => {
    const pipelineFromUrl = searchParams.get('pipeline') || '';
    if (pipelineFromUrl !== selected) {
      setSelected(pipelineFromUrl);
      if (!pipelineFromUrl) setData(null);
    }
  }, [searchParams]);

  useEffect(() => {
    if (!selected) return;
    setLoading(true);
    setError(null);
    getMetrics(selected).then(setData).catch(e => setError(e.message)).finally(() => setLoading(false));
  }, [selected]);

  const handleFeedback = async (metricName: string, helpful: string) => {
    if (!selected) return;
    await submitFeedback(selected, metricName, helpful);
    setData(prev => prev ? { ...prev, feedback: { ...prev.feedback, [metricName]: helpful } } : prev);
  };

  return (
    <>
    <AppLayout
      navigationHide
      toolsHide
      contentType="default"
      content={
        <ContentLayout
          header={
            <Header
              variant="h1"
              description="Analyze pipelines and discover missing business metrics using AI-powered recommendations"
              actions={<Button onClick={() => navigate(-1)}>← Back</Button>}
            >
              <Link variant="primary" fontSize="heading-xl" onFollow={(e) => { e.preventDefault(); setSelected(''); setData(null); setSearchParams({}); }}>🔍 Business Metrics Recommender</Link>
            </Header>
          }
        >
          <SpaceBetween size="l">
            <PipelineSelector pipelines={pipelines} selected={selected} onSelect={handleSelect} onRequestClick={() => setRequestModalVisible(true)} onViewRequestsClick={() => { getAnalysisRequests().then(setRequests); setViewRequestsVisible(true); }} />
            {error && <Alert type="error" header="Failed to load data">{error}</Alert>}
            {loading && (
              <Box textAlign="center" padding={{ vertical: 'xxl' }}>
                <SpaceBetween size="s" alignItems="center">
                  <Spinner size="large" />
                  <Box color="text-status-inactive">Loading metrics for {selected}...</Box>
                </SpaceBetween>
              </Box>
            )}
            {!selected && !loading && (
              <Box textAlign="center" padding={{ vertical: 'xxl' }} color="text-status-inactive">
                <Box variant="h3" color="inherit">Select a pipeline to get started</Box>
                <Box variant="p" color="inherit">Choose a pipeline above to see its missing business metrics analysis</Box>
              </Box>
            )}
            {data && !loading && <MetricsDashboard data={data} onFeedback={handleFeedback} />}
          </SpaceBetween>
        </ContentLayout>
      }
    />
    <Modal
      visible={requestModalVisible}
      onDismiss={() => setRequestModalVisible(false)}
      header="Request Pipeline Analysis"
      footer={
        <SpaceBetween size="xs" direction="horizontal">
          <Button variant="link" onClick={() => setRequestModalVisible(false)}>Cancel</Button>
          <Button variant="primary" loading={requestSubmitting} onClick={async () => {
            setRequestError('');
            const errors = [];
            if (!requestPipeline.trim()) errors.push('Pipeline / Service name is required');
            if (!requestAlias.trim()) errors.push('Your alias is required');
            if (errors.length > 0) { setRequestError(errors.join('. ')); return; }
            setRequestSubmitting(true);
            await submitAnalysisRequest(requestPipeline.trim(), requestAlias.trim());
            const updated = await getAnalysisRequests();
            setRequests(updated);
            setRequestPipeline('');
            setRequestAlias('');
            setRequestSubmitting(false);
            setRequestSuccess(true);
            setTimeout(() => { setRequestSuccess(false); setRequestModalVisible(false); }, 2000);
          }}>Submit Request</Button>
        </SpaceBetween>
      }
    >
      <SpaceBetween size="m">
        {requestSuccess && <Alert type="success">Request submitted successfully! We'll analyze your pipeline soon.</Alert>}
        {requestError && <Alert type="error">{requestError}</Alert>}
        <FormField label="Pipeline / Service name" errorText={!requestPipeline.trim() && requestError ? 'Required' : ''}>
          <Input value={requestPipeline} onChange={({ detail }) => { setRequestPipeline(detail.value); setRequestError(''); }} placeholder="e.g., FlowRebinService" />
        </FormField>
        <FormField label="Your alias" errorText={!requestAlias.trim() && requestError ? 'Required' : ''}>
          <Input value={requestAlias} onChange={({ detail }) => { setRequestAlias(detail.value); setRequestError(''); }} placeholder="e.g., samasamb" />
        </FormField>
      </SpaceBetween>
    </Modal>
    <Modal
      visible={viewRequestsVisible}
      onDismiss={() => setViewRequestsVisible(false)}
      header="All Pipeline Analysis Requests"
      size="large"
    >
      {requests.length === 0 ? (
        <Box textAlign="center" color="text-status-inactive" padding="l">No requests yet</Box>
      ) : (
        <Table
          items={requests}
          columnDefinitions={[
            { id: 'pipeline', header: 'Pipeline', cell: (r: AnalysisRequest) => r.pipeline },
            { id: 'requestedBy', header: 'Requested By', cell: (r: AnalysisRequest) => r.requestedBy },
            { id: 'status', header: 'Status', cell: (r: AnalysisRequest) => <StatusIndicator type={r.status === 'Done' ? 'success' : 'pending'}>{r.status}</StatusIndicator> },
            { id: 'date', header: 'Requested On', cell: (r: AnalysisRequest) => new Date(r.requestedAt).toLocaleString() },
            { id: 'actions', header: '', cell: (r: AnalysisRequest) => (
              <SpaceBetween size="xs" direction="horizontal">
                {r.status !== 'Done' && <span style={{ cursor: 'pointer', fontSize: '18px' }} onClick={async () => { await updateRequestStatus(r.pipeline, r.requestedAt, 'Done'); setRequests(await getAnalysisRequests()); }}>✅</span>}
                <span style={{ cursor: 'pointer', fontSize: '18px' }} onClick={async () => { await deleteAnalysisRequest(r.pipeline, r.requestedAt); setRequests(await getAnalysisRequests()); }}>🗑️</span>
              </SpaceBetween>
            )},
          ]}
          sortingDisabled
        />
      )}
    </Modal>
    </>
  );
};

export default MetricsRecommenderPage;
